import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../services/api';
import { BACKEND_URL } from '../config';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';
import { Plus, Minus, Search, X, Users, CreditCard, Wallet, FileText, Phone, Mail, Edit, Trash2, ClipboardList, Banknote, Printer, Smartphone, ArrowLeft, ArrowRight, Inbox } from 'lucide-react';
import logger from '../utils/logger';

const CLIENT_TYPE_LABELS = {
  B2C: 'B2C · Particulier',
  B2B: 'B2B · Entreprise / professionnel',
  B2G: 'B2G · Institution publique',
  B2F: 'B2F · International'
};

const BusinessClients = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const canManage = projectMode ? outletContext?.canManage : (outletContext?.canManageClients ?? true);
  
  // Base URL de l'API selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState({
    nom: '', telephone: '', email: '', adresse: '', notes: '', credit_autorise: 0,
    type_client: 'B2C', ncc: '', pays: ''
  });
  const [creditAction, setCreditAction] = useState({ type: 'credit', montant: '', notes: '', mode: 'cash' });
  const [creditTab, setCreditTab] = useState('action'); // 'action' | 'history'
  const [creditHistory, setCreditHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [settings, setSettings] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadClients = async () => {
    try {
      const res = await api.get(`${apiBase}/clients`);
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedClient) {
        await api.put(`${apiBase}/clients/${selectedClient.id}`, formData);
      } else {
        await api.post(`${apiBase}/clients`, formData);
      }
      loadClients();
      closeModal();
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleCreditSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`${apiBase}/clients/${selectedClient.id}/credit`, creditAction);
      loadClients();
      setShowCreditModal(false);
    } catch (err) {
      logger.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm('Supprimer ce client ?', { type: 'danger' })) return;
    try {
      await api.delete(`${apiBase}/clients/${id}`);
      loadClients();
    } catch (err) {
      logger.error(err);
    }
  };

  const openModal = (client = null) => {
    if (client) {
      setSelectedClient(client);
      setFormData({
        nom: client.nom || '',
        telephone: client.telephone || '',
        email: client.email || '',
        adresse: client.adresse || '',
        notes: client.notes || '',
        credit_autorise: client.credit_autorise || 0,
        type_client: client.type_client || 'B2C',
        ncc: client.ncc || '',
        pays: client.pays || ''
      });
    } else {
      setSelectedClient(null);
      setFormData({
        nom: '', telephone: '', email: '', adresse: '', notes: '', credit_autorise: 0,
        type_client: 'B2C', ncc: '', pays: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedClient(null);
  };

  const openCreditModal = (client) => {
    setSelectedClient(client);
    setCreditAction({ type: 'credit', montant: '', notes: '', mode: 'cash' });
    setCreditTab('action');
    setCreditHistory([]);
    setShowCreditModal(true);
  };

  const loadCreditHistory = async (clientId) => {
    setLoadingHistory(true);
    try {
      const [histRes, settRes] = await Promise.all([
        api.get(`${apiBase}/clients/${clientId}/credit-history`),
        settings ? Promise.resolve({ data: settings }) : api.get(`${apiBase}/settings`).catch(() => ({ data: null }))
      ]);
      setCreditHistory(Array.isArray(histRes.data) ? histRes.data : []);
      if (!settings && settRes.data) setSettings(settRes.data);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handlePrintHistory = () => {
    const content = printRef.current;
    if (!content) return;
    const originalBody = document.body.innerHTML;
    document.body.innerHTML = content.innerHTML;
    window.print();
    document.body.innerHTML = originalBody;
    window.location.reload();
  };

  const filteredClients = clients.filter(c =>
    c.nom?.toLowerCase().includes(search.toLowerCase()) ||
    c.telephone?.includes(search) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.ncc?.toLowerCase().includes(search.toLowerCase()) ||
    c.pays?.toLowerCase().includes(search.toLowerCase()) ||
    c.type_client?.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Clients</h1>
          <p className="text-gray-500 text-sm">Gérez vos clients et leurs crédits</p>
        </div>
        {canManage && (
          <button
            onClick={() => openModal()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center justify-center gap-2"
          >
            <Plus size={16} /> Nouveau client
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Search size={14} /></span>
          <input
            type="text"
            placeholder="Rechercher un client (nom, téléphone, email)..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-400 focus:outline-none"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Résultat de recherche */}
      {search && (
        <div className="mb-4 text-sm text-gray-500">
          {filteredClients.length} résultat(s) pour "{search}"
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-blue-500 text-xl md:text-2xl mb-1 md:mb-2"><Users size={24} /></div>
          <div className="text-xl md:text-2xl font-bold text-gray-800">{clients.length}</div>
          <div className="text-xs md:text-sm text-gray-500">Total clients</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-orange-500 text-xl md:text-2xl mb-1 md:mb-2"><CreditCard size={24} /></div>
          <div className="text-xl md:text-2xl font-bold text-orange-600">
            {clients.filter(c => c.solde_credit > 0).length}
          </div>
          <div className="text-xs md:text-sm text-gray-500">Avec crédit</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-red-500 text-xl md:text-2xl mb-1 md:mb-2"><Wallet size={24} /></div>
          <div className="text-lg md:text-2xl font-bold text-red-600">
            <span className="hidden sm:inline">{clients.reduce((sum, c) => sum + (parseFloat(c.solde_credit) || 0), 0).toLocaleString()}</span>
            <span className="sm:hidden">{(clients.reduce((sum, c) => sum + (parseFloat(c.solde_credit) || 0), 0) / 1000).toFixed(0)}k</span>
            <span className="text-xs md:text-sm"> XOF</span>
          </div>
          <div className="text-xs md:text-sm text-gray-500">Total crédits</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-amber-500 text-xl md:text-2xl mb-1 md:mb-2"><FileText size={24} /></div>
          <div className="text-lg md:text-2xl font-bold text-amber-600">
            <span className="hidden sm:inline">{clients.reduce((sum, c) => sum + (parseFloat(c.montant_du) || 0), 0).toLocaleString()}</span>
            <span className="sm:hidden">{(clients.reduce((sum, c) => sum + (parseFloat(c.montant_du) || 0), 0) / 1000).toFixed(0)}k</span>
            <span className="text-xs md:text-sm"> XOF</span>
          </div>
          <div className="text-xs md:text-sm text-gray-500">Factures impayées</div>
        </div>
      </div>

      {/* Clients List */}
      {paginatedClients.length > 0 ? (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Client</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Contact</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Solde crédit</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Montant dû</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paginatedClients.map(client => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{client.nom}</div>
                      <span className="mt-1 inline-flex rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                        {CLIENT_TYPE_LABELS[client.type_client] || CLIENT_TYPE_LABELS.B2C}
                      </span>
                      {client.ncc && <div className="mt-1 text-xs text-gray-500">NCC : {client.ncc}</div>}
                      {client.pays && <div className="mt-1 text-xs text-gray-500">Pays : {client.pays}</div>}
                      {client.adresse && <div className="text-xs text-gray-400">{client.adresse}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {client.telephone && <div className="text-sm text-gray-600"><Phone size={14} className="inline" /> {client.telephone}</div>}
                      {client.email && <div className="text-sm text-gray-500"><Mail size={14} className="inline" /> {client.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${client.solde_credit > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        {(client.solde_credit || 0).toLocaleString()} XOF
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${parseFloat(client.montant_du) > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                        {(parseFloat(client.montant_du) || 0).toLocaleString()} XOF
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-2">
                        {canManage && (
                          <>
                            <button
                              onClick={() => openCreditModal(client)}
                              className="p-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition"
                              title="Gérer crédit"
                            >
                              <CreditCard size={16} />
                            </button>
                            <button
                              onClick={() => openModal(client)}
                              className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition"
                              title="Modifier"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(client.id)}
                              className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                              title="Supprimer"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {paginatedClients.map(client => (
              <div key={client.id} className="bg-white rounded-xl shadow p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-medium text-gray-800">{client.nom}</div>
                    <span className="mt-1 inline-flex rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                      {CLIENT_TYPE_LABELS[client.type_client] || CLIENT_TYPE_LABELS.B2C}
                    </span>
                    {client.ncc && <div className="mt-1 text-xs text-gray-500">NCC : {client.ncc}</div>}
                    {client.pays && <div className="mt-1 text-xs text-gray-500">Pays : {client.pays}</div>}
                    {client.telephone && <div className="text-sm text-gray-500"><Phone size={14} className="inline" /> {client.telephone}</div>}
                    {client.email && <div className="text-xs text-gray-400 truncate max-w-[180px]"><Mail size={14} className="inline" /> {client.email}</div>}
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-bold block ${client.solde_credit > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {(client.solde_credit || 0).toLocaleString()} XOF
                    </span>
                    {parseFloat(client.montant_du) > 0 && (
                      <span className="text-xs font-semibold text-amber-600 block mt-0.5">
                        Doit: {parseFloat(client.montant_du).toLocaleString()} XOF
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-end items-center border-t pt-3">
                  {canManage && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => openCreditModal(client)}
                        className="p-2 bg-orange-50 text-orange-600 rounded-lg"
                      >
                        <CreditCard size={16} />
                      </button>
                      <button
                        onClick={() => openModal(client)}
                        className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="p-2 bg-red-50 text-red-600 rounded-lg"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <span className="text-6xl mb-4 block"><Users size={40} /></span>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {search ? 'Aucun résultat' : 'Aucun client'}
          </h3>
          <p className="text-gray-500 mb-4">
            {search ? `Aucun client ne correspond à "${search}"` : 'Commencez par ajouter vos clients'}
          </p>
          {!search && canManage && (
            <button
              onClick={() => openModal()}
              className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
            >
              Ajouter un client
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className={`px-3 py-2 rounded-lg ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            ««
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            <ArrowLeft size={16} className="inline" /> Précédent
          </button>
          
          <div className="flex gap-1 items-center">
            {(() => {
              const pages = [];
              const showPages = new Set();
              showPages.add(1);
              showPages.add(totalPages);
              for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                showPages.add(i);
              }
              const sortedPages = [...showPages].sort((a, b) => a - b);
              sortedPages.forEach((page, idx) => {
                if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                  pages.push(<span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>);
                }
                pages.push(
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 rounded-lg ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
                  >
                    {page}
                  </button>
                );
              });
              return pages;
            })()}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            Suivant <ArrowRight size={16} className="inline" />
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 rounded-lg ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            »»
          </button>
        </div>
      )}

      {/* Modal Client */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:items-center">
          <div className="my-auto flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="shrink-0 border-b p-5 sm:p-6">
              <h2 className="text-xl font-bold text-gray-800">
                {selectedClient ? 'Modifier le client' : 'Nouveau client'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="min-h-0 overflow-y-auto overscroll-contain p-5 space-y-4 sm:p-6">
              <div>
                <label htmlFor="client-type" className="block text-sm font-medium text-gray-700 mb-1">Type de client *</label>
                <select
                  id="client-type"
                  value={formData.type_client || 'B2C'}
                  onChange={e => setFormData({ ...formData, type_client: e.target.value })}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                >
                  <option value="B2C">B2C — Particulier</option>
                  <option value="B2B">B2B — Entreprise / professionnel</option>
                  <option value="B2G">B2G — Institution publique</option>
                  <option value="B2F">B2F — Client à l’international</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {formData.type_client === 'B2B' && 'Le NCC sera requis pour les clients professionnels.'}
                  {formData.type_client === 'B2G' && 'Utilisez le nom officiel de l’administration ou de l’institution.'}
                  {formData.type_client === 'B2F' && 'Indiquez le pays du client international.'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.type_client === 'B2B' ? 'Nom de l’entreprise / du professionnel *' :
                    formData.type_client === 'B2G' ? 'Nom de l’institution *' : 'Nom du client *'}
                </label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              {formData.type_client === 'B2B' && (
                <div>
                  <label htmlFor="client-ncc" className="block text-sm font-medium text-gray-700 mb-1">NCC *</label>
                  <input
                    id="client-ncc"
                    type="text"
                    maxLength={50}
                    value={formData.ncc}
                    onChange={e => setFormData({ ...formData, ncc: e.target.value })}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                    placeholder="Numéro de compte contribuable"
                  />
                </div>
              )}

              {formData.type_client === 'B2F' && (
                <div>
                  <label htmlFor="client-country" className="block text-sm font-medium text-gray-700 mb-1">Pays *</label>
                  <input
                    id="client-country"
                    type="text"
                    maxLength={100}
                    value={formData.pays}
                    onChange={e => setFormData({ ...formData, pays: e.target.value })}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                    placeholder="Pays de résidence ou d’activité"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input
                  type="text"
                  value={formData.adresse}
                  onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
                >
                  {selectedClient ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Crédit */}
      {showCreditModal && selectedClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Crédit client</h2>
                  <p className="text-gray-500">{selectedClient.nom}</p>
                </div>
                <button onClick={() => setShowCreditModal(false)} className="text-gray-400 hover:text-gray-600 text-xl"><X size={14} /></button>
              </div>
              <div className="mt-3 p-3 bg-orange-50 rounded-lg">
                <div className="text-sm text-gray-600">Solde actuel</div>
                <div className="text-2xl font-bold text-orange-600">
                  {(selectedClient.solde_credit || 0).toLocaleString()} XOF
                </div>
              </div>
              {/* Tabs */}
              <div className="flex gap-1 mt-4 bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => setCreditTab('action')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${creditTab === 'action' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}
                >
                  <CreditCard size={16} className="inline" /> Opération
                </button>
                <button
                  onClick={() => { setCreditTab('history'); if (creditHistory.length === 0) loadCreditHistory(selectedClient.id); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${creditTab === 'history' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}
                >
                  <ClipboardList size={16} className="inline" /> Historique
                </button>
              </div>
            </div>

            {/* Tab Opération */}
            {creditTab === 'action' && (
              <form onSubmit={handleCreditSubmit} className="p-6 space-y-4">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setCreditAction({...creditAction, type: 'credit'})}
                    className={`flex-1 py-3 rounded-xl font-medium transition ${
                      creditAction.type === 'credit'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <Plus size={16} className="inline" /> Ajouter crédit
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreditAction({...creditAction, type: 'remboursement'})}
                    className={`flex-1 py-3 rounded-xl font-medium transition ${
                      creditAction.type === 'remboursement'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <Minus size={16} className="inline" /> Remboursement
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant (XOF)</label>
                  <input
                    type="number"
                    min="1"
                    value={creditAction.montant}
                    onChange={(e) => setCreditAction({...creditAction, montant: e.target.value})}
                    required
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none text-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <input
                    type="text"
                    value={creditAction.notes}
                    onChange={(e) => setCreditAction({...creditAction, notes: e.target.value})}
                    placeholder="Motif..."
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>

                {creditAction.type === 'remboursement' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mode de remboursement</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setCreditAction({...creditAction, mode: 'cash'})}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition border-2 ${
                          creditAction.mode === 'cash'
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 text-gray-500'
                        }`}
                      >
                        <Banknote size={16} className="inline" /> Cash (sortie de caisse)
                      </button>
                      <button
                        type="button"
                        onClick={() => setCreditAction({...creditAction, mode: 'ajustement'})}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition border-2 ${
                          creditAction.mode === 'ajustement'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-500'
                        }`}
                      >
                        <FileText size={14} className="inline" /> Ajustement comptable
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5">
                      {creditAction.mode === 'cash'
                        ? 'Le montant sera déduit de la trésorerie (argent remis au client)'
                        : 'Simple correction du solde, sans impact sur la trésorerie'}
                    </p>
                  </div>
                )}

                {creditAction.type === 'credit' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Origine du crédit</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setCreditAction({...creditAction, mode: 'cash'})}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition border-2 ${
                          creditAction.mode === 'cash'
                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                            : 'border-gray-200 text-gray-500'
                        }`}
                      >
                        <Banknote size={16} className="inline" /> Cash reçu du client
                      </button>
                      <button
                        type="button"
                        onClick={() => setCreditAction({...creditAction, mode: 'ajustement'})}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition border-2 ${
                          creditAction.mode === 'ajustement'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-500'
                        }`}
                      >
                        <FileText size={14} className="inline" /> Ajustement comptable
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5">
                      {creditAction.mode === 'cash'
                        ? 'Le client a versé de l\'argent (acompte, trop-perçu...) -> ajouté à la trésorerie'
                        : 'Geste commercial, compensation... sans mouvement de caisse'}
                    </p>
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <span className="text-gray-500">Nouveau solde: </span>
                  <span className={`font-bold text-lg ${
                    creditAction.type === 'credit'
                      ? 'text-orange-600'
                      : 'text-green-600'
                  }`}>
                    {creditAction.type === 'credit'
                      ? (parseFloat(selectedClient.solde_credit || 0) + parseFloat(creditAction.montant || 0)).toLocaleString()
                      : Math.max(0, parseFloat(selectedClient.solde_credit || 0) - parseFloat(creditAction.montant || 0)).toLocaleString()
                    } XOF
                  </span>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCreditModal(false)}
                    className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className={`flex-1 py-2 text-white rounded-xl transition ${
                      creditAction.type === 'credit'
                        ? 'bg-orange-600 hover:bg-orange-700'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    Confirmer
                  </button>
                </div>
              </form>
            )}

            {/* Tab Historique */}
            {creditTab === 'history' && (
              <div className="p-6">
                {loadingHistory ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
                  </div>
                ) : creditHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <Inbox className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500">Aucun historique de crédit</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-end mb-3">
                      <button onClick={handlePrintHistory} className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition text-sm flex items-center gap-2">
                        <Printer size={16} /> Imprimer
                      </button>
                    </div>
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {creditHistory.map((entry) => (
                        <div key={entry.id} className={`p-3 rounded-xl border ${entry.type === 'credit' ? 'border-orange-200 bg-orange-50' : 'border-green-200 bg-green-50'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-sm font-semibold ${entry.type === 'credit' ? 'text-orange-700' : 'text-green-700'}`}>
                              {entry.type === 'credit' ? <><Plus size={16} className="inline" /> Crédit</> : <><Minus size={16} className="inline" /> Remboursement</>}
                            </span>
                            <span className={`font-bold ${entry.type === 'credit' ? 'text-orange-600' : 'text-green-600'}`}>
                              {entry.type === 'credit' ? '+' : '-'}{parseFloat(entry.montant).toLocaleString()} XOF
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>{new Date(entry.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                            <span>Solde: {parseFloat(entry.solde_apres).toLocaleString()} XOF</span>
                          </div>
                          {entry.notes && <div className="text-xs text-gray-600 mt-1">{entry.notes}</div>}
                          {entry.reference_type && <div className="text-xs text-gray-400 mt-0.5">Réf: {entry.reference_type} #{entry.reference_id}</div>}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Zone d'impression cachée */}
            <div style={{ display: 'none' }}>
              <div ref={printRef}>
                <div style={{ fontFamily: 'Arial, sans-serif', padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
                  {/* En-tête entreprise - style facture */}
                  {settings && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #333' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                        {settings.logo && (
                          <img 
                            src={settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`} 
                            alt="Logo" 
                            style={{ width: '80px', height: '80px', objectFit: 'contain', borderRadius: '8px' }} 
                          />
                        )}
                        <div>
                          <h2 style={{ margin: '0 0 5px 0', fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>{settings.nom_entreprise || 'Mon Entreprise'}</h2>
                          {settings.adresse && <div style={{ fontSize: '12px', color: '#666' }}>{settings.adresse}</div>}
                          {settings.telephone && <div style={{ fontSize: '12px', color: '#666' }}><Phone size={14} className="inline" /> {settings.telephone}</div>}
                          {settings.email && <div style={{ fontSize: '12px', color: '#666' }}><Mail size={14} className="inline" /> {settings.email}</div>}
                          {settings.numero_registre && <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>RCCM: {settings.numero_registre}</div>}
                          {settings.numero_fiscal && <div style={{ fontSize: '11px', color: '#999' }}>NIF: {settings.numero_fiscal}</div>}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <h1 style={{ margin: '0', fontSize: '22px', fontWeight: 'bold', color: '#4f46e5' }}>HISTORIQUE DES CRÉDITS</h1>
                        <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                          <div>Date: <strong>{new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</strong></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Informations client */}
                  <div style={{ backgroundColor: '#f9fafb', padding: '15px', marginBottom: '20px', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Informations client</div>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f2937' }}>{selectedClient?.nom}</div>
                        {selectedClient?.adresse && <div style={{ fontSize: '12px', color: '#666' }}>{selectedClient.adresse}</div>}
                        {selectedClient?.telephone && <div style={{ fontSize: '12px', color: '#666' }}><Smartphone size={14} className="inline" /> {selectedClient.telephone}</div>}
                        {selectedClient?.email && <div style={{ fontSize: '12px', color: '#666' }}><Mail size={14} className="inline" /> {selectedClient.email}</div>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Solde crédit</div>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ea580c' }}>{(selectedClient?.solde_credit || 0).toLocaleString()} XOF</div>
                      </div>
                    </div>
                  </div>

                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Date</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Type</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Montant</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Solde après</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Référence</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditHistory.map((entry) => (
                        <tr key={entry.id}>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            {new Date(entry.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px', color: entry.type === 'credit' ? '#ea580c' : '#16a34a', fontWeight: 'bold' }}>
                            {entry.type === 'credit' ? 'Crédit' : 'Remboursement'}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', color: entry.type === 'credit' ? '#ea580c' : '#16a34a' }}>
                            {entry.type === 'credit' ? '+' : '-'}{parseFloat(entry.montant).toLocaleString()} XOF
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                            {parseFloat(entry.solde_apres).toLocaleString()} XOF
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>
                            {entry.reference_type ? `${entry.reference_type} #${entry.reference_id}` : '-'}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '11px' }}>
                            {entry.notes || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ backgroundColor: '#f9fafb', fontWeight: 'bold' }}>
                        <td colSpan="2" style={{ border: '1px solid #ddd', padding: '8px' }}>
                          Total ({creditHistory.length} opérations)
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}></td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', color: '#ea580c' }}>
                          {(selectedClient?.solde_credit || 0).toLocaleString()} XOF
                        </td>
                        <td colSpan="2" style={{ border: '1px solid #ddd', padding: '8px' }}></td>
                      </tr>
                    </tfoot>
                  </table>
                  <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ textAlign: 'center', width: '45%' }}>
                      <div style={{ borderTop: '1px solid #999', paddingTop: '8px', fontSize: '12px' }}>Signature client</div>
                    </div>
                    <div style={{ textAlign: 'center', width: '45%' }}>
                      <div style={{ borderTop: '1px solid #999', paddingTop: '8px', fontSize: '12px' }}>Signature entreprise</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessClients;
